import { isBrowser, isReactSnap } from './runtime';

export type CookieConsentChoice = 'accepted' | 'rejected';

export const COOKIE_CONSENT_STORAGE_KEY = 'layerre_cookie_consent';
export const COOKIE_SETTINGS_EVENT = 'layerre:open-cookie-settings';

const TRUSTVIEWS_TOKEN = 'a884881d-4626-4039-b2fd-0b64de8f77d1';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

function ensureGtag(): void {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

export function getCookieConsent(): CookieConsentChoice | null {
  if (!isBrowser()) return null;
  try {
    const value = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (value === 'accepted' || value === 'rejected') return value;
  } catch {
    // localStorage can fail in private mode
  }
  return null;
}

export function openCookieSettings(): void {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}

function updateGoogleConsent(granted: boolean): void {
  ensureGtag();
  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

function loadGtm(): void {
  if (!isBrowser() || document.getElementById('layerre-gtm-script')) return;
  const script = document.createElement('script');
  script.id = 'layerre-gtm-script';
  script.async = true;
  script.src = `${process.env.PUBLIC_URL || ''}/js/analytics.js`;
  document.body.appendChild(script);
}

function loadTrustViews(): void {
  if (!isBrowser() || document.getElementById('layerre-trustviews-script')) {
    return;
  }
  const script = document.createElement('script');
  script.id = 'layerre-trustviews-script';
  script.defer = true;
  script.src = 'https://trustviews.io/script.js';
  script.setAttribute('data-token', TRUSTVIEWS_TOKEN);
  document.body.appendChild(script);
}

export function applyAnalyticsConsent(choice: CookieConsentChoice): void {
  if (!isBrowser() || isReactSnap()) return;

  if (choice === 'accepted') {
    updateGoogleConsent(true);
    loadGtm();
    loadTrustViews();
    return;
  }

  updateGoogleConsent(false);
}

export function setCookieConsent(choice: CookieConsentChoice): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, choice);
  } catch {
    // ignore
  }
  applyAnalyticsConsent(choice);
}

/** Load analytics on return visits if the user already accepted. */
export function initCookieConsent(): void {
  if (!isBrowser() || isReactSnap()) return;
  const existing = getCookieConsent();
  if (existing === 'accepted') {
    applyAnalyticsConsent('accepted');
  }
}
